import * as PIXI from 'pixi.js';
import { soundManager } from './soundManager';
import { InfoPopUp } from './infoPopUp';
import { HelpPopUp } from './helpPopUp';
import { languageManager } from './languageManager';

export class MenuPopUp extends PIXI.Container {
  constructor(sheet, onClose, assetLoader) {
    super();
    this.sheet = sheet;
    this.onClose = onClose;
    this.assetLoader = assetLoader;
    this.interactive = true; 
    this.visible = false;
    this.zIndex = 1000;

    this.onResize = this.resize.bind(this);
    window.addEventListener('resize', this.onResize);

    this.createPopup();
  }

  destroy(options){
    window.removeEventListener('resize', this.onResize);
    super.destroy(options);
  }

  createPopup() {

    this.overlay = new PIXI.Graphics();
    this.addChild(this.overlay);
    
    this.popupBg = new PIXI.Graphics();
    this.addChild(this.popupBg);

    const btnData = [
      { key: 'sounds', img: 'sounds.png', label: 'Sounds' },
      { key: 'music', img: 'music.png', label: 'Music' },
      { key: 'language', img: 'language.png', label: 'Language' },
      { key: 'history', img: 'history.png', label: 'History' },
      { key: 'help', img: 'help.png', label: 'Help' },
      { key: 'i', img: 'i.png', label: 'i' },
    ];

    this.buttons = [];
    btnData.forEach((btn, i) => {
      const sprite = PIXI.Sprite.from(this.sheet.textures[btn.img]);
      sprite.anchor.set(0.5);
      sprite.interactive = true;
      sprite.cursor = 'pointer';
      this.buttons.push({ sprite, data: btn });
      if (btn.key === 'sounds') {
        this.soundsBtn = sprite;
        sprite.on('pointertap', () => {
          if (!soundManager.isMuted()) {
            sprite.texture = this.sheet.textures['ni sounds.png'];
            soundManager.mute();
          } else {
            sprite.texture = this.sheet.textures['sounds.png'];
            soundManager.unmute();
          }
        });
      } else if (btn.key === 'music') {
        this.musicBtn = sprite;
        let musicOff = false;
        sprite.on('pointertap', () => {
          musicOff = !musicOff;
          if (musicOff) {
            sprite.texture = this.sheet.textures['musicno.png'];
            soundManager.stopBgSound();
          } else {
            sprite.texture = this.sheet.textures['music.png'];
            soundManager.playBgSound();
          }
        });
      } else if (btn.key === 'language') {
        this.languageBtn = sprite;
        sprite.on('pointertap', () => {
          this.showLanguageGrid();
        });
      } else if (btn.key === 'help') {
        this.helpBtn = sprite;
        sprite.on('pointertap', () => {
          this.showHelpPopUp();
        });
      } else if (btn.key === 'i') {
        this.infoBtn = sprite;
        sprite.on('pointertap', () => {
          this.showInfoPopUp();
        });
      }
      this.addChild(sprite);
    });

    this.languageGridOverlay = new PIXI.Container();
    this.languageGridOverlay.visible = false;
    this.addChild(this.languageGridOverlay);

    const langBg = new PIXI.Graphics();
    langBg.beginFill(0x222a33, 1);
    langBg.drawRect(0, 0, window.innerWidth, window.innerHeight);
    langBg.endFill();
    this.languageGridOverlay.addChild(langBg);

    const languages = [
      { code: 'en', label: 'English' },
      { code: 'fr', label: 'Français' },
      { code: 'it', label: 'Italiano' },
    ];
    const langGridRows = 3;
    const langGridCols = 3;
    const cellW = 320;
    const cellH = 140;
    const cellSpacingX = 40;
    const cellSpacingY = 30;
    const gridTotalW = langGridCols * cellW + (langGridCols - 1) * cellSpacingX;
    const gridTotalH = langGridRows * cellH + (langGridRows - 1) * cellSpacingY;
    const gridStartX2 = (window.innerWidth - gridTotalW) / 2;
    const gridStartY2 = (window.innerHeight - gridTotalH) / 2;
    
    // Store language cells for easy access
    this.languageCells = new Map();
    
    languages.forEach((lang, i) => {
      const row = Math.floor(i / langGridCols);
      const col = i % langGridCols;
      const cell = new PIXI.Container();
      cell.x = gridStartX2 + col * (cellW + cellSpacingX);
      cell.y = gridStartY2 + row * (cellH + cellSpacingY);
      cell.interactive = true;
      cell.cursor = 'pointer';
      
      const cellBg = new PIXI.Graphics();
      // Set initial color based on current language
      const isCurrentLanguage = languageManager.getCurrentLanguage() === lang.code;
      const bgColor = isCurrentLanguage ? 0x4a90e2 : 0x38404a; // Blue for selected, gray for unselected
      cellBg.beginFill(bgColor);
      cellBg.drawRect(0, 0, cellW, cellH);
      cellBg.endFill();
      cell.addChild(cellBg);
      
      // Store reference to background for color updates
      cell.cellBg = cellBg;
      cell.langCode = lang.code;
      cell.cellWidth = cellW;
      cell.cellHeight = cellH;
      
      // Label
      const style = new PIXI.TextStyle({
        fontFamily: 'Arial',
        fontSize: 48,
        fill: '#eae9e2',
        fontWeight: 'bold',
        align: 'center',
      });
      const label = new PIXI.Text(lang.label, style);
      label.anchor.set(0.5);
      label.x = cellW / 2;
      label.y = cellH / 2;
      cell.addChild(label);
      
      // Handle language change
      cell.on('pointertap', () => {
        // Change language
        languageManager.setLanguage(lang.code);
        this.hideLanguageGrid();
      });
      
      this.languageGridOverlay.addChild(cell);
      this.languageCells.set(lang.code, cell);
    });
    
    // Listen for language changes to update cell colors
    languageManager.addListener((newLanguage) => {
      this.updateLanguageCellColors(newLanguage);
    });
    // Close button for language grid
    const langCloseBtn = PIXI.Sprite.from(this.sheet.textures['close.png']);
    langCloseBtn.anchor.set(1, 0);
    langCloseBtn.scale.set(0.7);
    langCloseBtn.interactive = true;
    langCloseBtn.cursor = 'pointer';
    langCloseBtn.on('pointertap', () => {
      this.hideLanguageGrid();
    });
    this.languageGridOverlay.addChild(langCloseBtn);
    this.langCloseBtn = langCloseBtn;

    // Close button
    const closeBtn = PIXI.Sprite.from(this.sheet.textures['close.png']);
    closeBtn.anchor.set(1, 0);
    closeBtn.interactive = true;
    closeBtn.cursor = 'pointer';
    closeBtn.on('pointertap', () => {
      this.hide();
      if (this.onClose) this.onClose();
    });
    this.addChild(closeBtn);
    this.menuCloseBtn = closeBtn;

    this.resize();
  }

  resize() {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    this.overlay.clear();
    this.overlay.beginFill(0x000000, 0.5);
    this.overlay.drawRect(0, 0, screenWidth, screenHeight);
    this.overlay.endFill();

    let popupWidth = 500;
    let popupHeight = 600;
    let btnScale = 0.6;
    let btnGridSpacingX = 140;
    let btnGridSpacingY = 140;
    let closeBtnScale = 0.5;

    if (screenWidth <= 480) { // Small mobile
      popupWidth = screenWidth * 0.9;
      popupHeight = screenHeight * 0.5;
      btnScale = 0.35;
      btnGridSpacingX = popupWidth / 3.5;
      btnGridSpacingY = popupHeight / 4;
      closeBtnScale = 0.35;
    } else if (screenWidth <= 767) { // Regular mobile
      popupWidth = screenWidth * 0.8;
      popupHeight = screenHeight * 0.6;
        btnScale = 0.45;
      btnGridSpacingX = popupWidth / 3.5;
      btnGridSpacingY = popupHeight / 4;
      closeBtnScale = 0.4;
    } else if (screenWidth <= 1024) { // Tablet
      popupWidth = 500;
      popupHeight = 400;
      btnScale = 0.5;
      btnGridSpacingX = 160;
      btnGridSpacingY = 140;
      closeBtnScale = 0.35;
    }

    this.popupBg.clear();
    this.popupBg.beginFill(0x222244, 0.95);
    this.popupBg.drawRoundedRect(0, 0, popupWidth, popupHeight, 30); 
    this.popupBg.endFill();
    this.popupBg.x = (screenWidth - popupWidth) / 2;
    this.popupBg.y = (screenHeight - popupHeight) / 2;

    const gridCols = 3;
    const gridStartX = this.popupBg.x + popupWidth / 2 - btnGridSpacingX;
    const gridStartY = this.popupBg.y + popupHeight / 4;

    this.buttons.forEach(({ sprite }, i) => {
      const row = Math.floor(i / gridCols);
      const col = i % gridCols;
      sprite.x = gridStartX + col * btnGridSpacingX;
      sprite.y = gridStartY + row * btnGridSpacingY;
      sprite.scale.set(btnScale);
    });

    this.menuCloseBtn.scale.set(closeBtnScale);
    this.menuCloseBtn.x = this.popupBg.x + popupWidth - 20 * closeBtnScale;
    this.menuCloseBtn.y = this.popupBg.y + 20 * closeBtnScale;

    // Responsive language grid
    this.resizeLanguageGrid(screenWidth, screenHeight);
  }

  resizeLanguageGrid(screenWidth, screenHeight) {
      if (!this.languageGridOverlay) return;

    this.langCloseBtn.x = screenWidth - 40;
    this.langCloseBtn.y = 40;

    // Recalculate grid layout
    let langGridCols = 3;
    let cellW = 320; 
    let cellH = 140;
    let cellSpacingX = 40;
    let cellSpacingY = 30;
    let fontSize = 48;

    if (screenWidth <= 480) {
        langGridCols = 1;
        cellW = screenWidth * 0.8;
        cellH = screenHeight * 0.1;
        cellSpacingX = 0.5;
        cellSpacingY = 5;
        fontSize = 28;
    } else if (screenWidth <= 767) {
        langGridCols = 1;
        cellW = screenWidth * 0.6;
        cellH = screenHeight * 0.12;
        cellSpacingX = 0;
        cellSpacingY = 20;
        fontSize = 32;
    } else if (screenWidth <= 1024) {
        langGridCols = 1;
        cellW = 240;
        cellH = 80;
        cellSpacingX = 40;
        cellSpacingY = 30;
        fontSize = 30;
    } else {
        langGridCols = 3;
        cellW = 320;
        cellH = 140;
        cellSpacingX = 40;
        cellSpacingY = 30;
        fontSize = 48;
    }

    // Adjust close button position for language grid
    this.langCloseBtn.x = screenWidth - (screenWidth <= 767 ? 20 : 40);
    this.langCloseBtn.y = (screenWidth <= 767 ? 10 : 40);
    this.langCloseBtn.scale.set(screenWidth <= 480 ? 0.4 : (screenWidth <= 767 ? 0.5 : 0.7));

    const gridTotalW = langGridCols * cellW + (langGridCols - 1) * cellSpacingX;
    const numRows = Math.ceil(this.languageCells.size / langGridCols);
    const gridTotalH = numRows * cellH + (numRows - 1) * cellSpacingY;
    const gridStartX2 = (screenWidth - gridTotalW) / 2;
    const gridStartY2 = (screenHeight - gridTotalH) / 2;

    let i = 0;
    this.languageCells.forEach((cell) => {
      const row = Math.floor(i / langGridCols);
      const col = i % langGridCols;
      cell.x = gridStartX2 + col * (cellW + cellSpacingX);
      cell.y = gridStartY2 + row * (cellH + cellSpacingY);
      //label.style.align = 'center';

      // Update cell background size
      if (cell.cellBg) {
        cell.cellBg.clear();
        const isCurrentLanguage = languageManager.getCurrentLanguage() === cell.langCode;
        const bgColor = isCurrentLanguage ? 0x4a90e2 : 0x38404a;
        cell.cellBg.beginFill(bgColor);
        cell.cellBg.drawRect(0, 0, cellW, cellH);
        cell.cellBg.endFill();
        cell.hitArea = new PIXI.Rectangle(0, 0, cellW, cellH);
      }

       // Update label font size and position
      const label = cell.children.find(child => child instanceof PIXI.Text);
      if (label) {
        label.style.fontSize = fontSize;
        label.style.wordWrap = true;
        label.style.wordWrapWidth = cellW * 0.95;
        label.style.align = 'center';
        label.x = cellW / 2;
        label.y = cellH / 2;
        label.updateText(); 
      }
      i++;
    });
  }       

  show() {
    this.visible = true;
    this.zIndex = 9999;
    if (this.parent) this.parent.setChildIndex(this, this.parent.children.length - 1);
    
    this.resize();

    // Ensure language grid colors are up to date
    if (this.languageCells && this.languageCells.size > 0) {
      this.updateLanguageCellColors(languageManager.getCurrentLanguage());
    }
    
    if (this.onShow) this.onShow();
  }

  hide() {
    this.visible = false;
    if (this.onHide) this.onHide();
  }

  showLanguageGrid() {
    this.debugLanguageGrid();
    
    this.languageGridOverlay.visible = true;
    if (this.menuCloseBtn) this.menuCloseBtn.visible = false;
    
    // Update colors when showing the grid to reflect current language
    this.updateLanguageCellColors(languageManager.getCurrentLanguage());
    
  }
  
  hideLanguageGrid() {
    this.languageGridOverlay.visible = false;
    if (this.menuCloseBtn) this.menuCloseBtn.visible = true;
  }

  showHelpPopUp() {
    if (!this.helpPopUp) {
      // Pass both 'buttons' and 'menubtns' sheets
      let buttonsSheet = null;
      let menubtnsSheet = null;
      if (this.assetLoader && this.assetLoader.getSheet) {
        buttonsSheet = this.assetLoader.getSheet('buttons');
        menubtnsSheet = this.assetLoader.getSheet('menubtns');
      }
      this.helpPopUp = new HelpPopUp(buttonsSheet, menubtnsSheet, () => {
        this.helpPopUp.hide();
      });
      if (this.parent) this.parent.addChild(this.helpPopUp);
    }
    this.helpPopUp.show();
  }

  showInfoPopUp() {
    if (!this.infoPopUp) {
      // Load gem symbol textures from the symbols sheet
      let symbolSheet = null;
      if (this.assetLoader && this.assetLoader.getSheet) {
        symbolSheet = this.assetLoader.getSheet('symbols');
      } else if (this.sheet && this.sheet.textures) {
        symbolSheet = this.sheet; // fallback
      }
      let symbolTextures = [];
      if (symbolSheet && symbolSheet.textures) {
        symbolTextures = Object.keys(symbolSheet.textures)
          .filter(key => key.endsWith('_static.png') && key !== 'symbol_base.png')
          .map(key => symbolSheet.textures[key]);
      }
      this.infoPopUp = new InfoPopUp(this.sheet, symbolTextures, () => {
        this.infoPopUp.hide();
      });
      if (this.parent) this.parent.addChild(this.infoPopUp);
    }
    this.infoPopUp.show();
  }

  updateLanguageCellColors(newLanguage) {
    if (!this.languageCells || this.languageCells.size === 0) {
      return;
    }
    
    
    this.languageCells.forEach((cell, code) => {
      if (!cell || !cell.cellBg) {
        console.warn(`Cell or cellBg not found for language: ${code}`);
        return;
      }
      
      const isCurrentLanguage = code === newLanguage;
      const bgColor = isCurrentLanguage ? 0x4a90e2 : 0x38404a; // Blue for selected, gray for unselected
      
      // Clear and redraw the background
      cell.cellBg.clear();
      cell.cellBg.beginFill(bgColor);
      cell.cellBg.drawRect(0, 0, cell.cellWidth || 320, cell.cellHeight || 140);
      cell.cellBg.endFill();
      
    });
  }
  
  // Debug method to check language grid status
  debugLanguageGrid() {
    
    if (this.languageCells) {
      this.languageCells.forEach((cell, code) => {
        console.log(`- Cell ${code}:`, {
          hasCell: !!cell,
          hasBg: !!(cell && cell.cellBg),
          width: cell ? cell.cellWidth : 'N/A',
          height: cell ? cell.cellHeight : 'N/A'
        });
      });
    }
  }
}
